import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Slide from '@mui/material/Slide';
import Fade from '@mui/material/Fade';
import { TransitionProps } from '@mui/material/transitions';
import { alpha } from '@mui/material/styles';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useLanguage } from '../../contexts/LanguageContext';
import { getInstallments, type Installment } from '../data/orders';
import InstallmentPaymentDialog from './InstallmentPaymentDialog';
import dayjs from 'dayjs';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface InstallmentSelectionDialogProps {
  open: boolean;
  orderId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function InstallmentSelectionDialog({
  open,
  orderId,
  onClose,
  onSuccess,
}: InstallmentSelectionDialogProps) {
  const { t } = useLanguage();
  const [installments, setInstallments] = React.useState<Installment[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [selectedInstallment, setSelectedInstallment] = React.useState<Installment | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      loadInstallments();
    }
  }, [open, orderId]);

  const loadInstallments = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getInstallments(orderId);
      // Filter to show only unpaid installments (status === 'DUE')
      const unpaidInstallments = data.filter((inst) => {
        // Primary check: status field
        if (inst.status) {
          return inst.status === 'DUE';
        }
        // Fallback: check paid field if status doesn't exist
        return inst.paid === false || !inst.paid;
      });
      setInstallments(unpaidInstallments);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  const handleInstallmentClick = (installment: Installment) => {
    setSelectedInstallment(installment);
    setPaymentDialogOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentDialogOpen(false);
    setSelectedInstallment(null);
    loadInstallments();
    if (onSuccess) {
      onSuccess();
    }
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialogOpen(false);
    setSelectedInstallment(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: (theme) => ({
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(2, 139, 203, 0.15)',
            background: theme.palette.background.paper,
            ...theme.applyStyles('dark', {
              background: alpha('#1a1a1a', 0.95),
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            }),
          }),
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            px: 3,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <ListAltIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
          {t('orders.selectInstallment')}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', py: 6 }}>
              <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('common.loading')}...
              </Typography>
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem',
                },
              }}
            >
              {error.message}
            </Alert>
          ) : installments.length === 0 ? (
            <Box
              sx={(theme) => ({
                py: 6,
                textAlign: 'center',
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.grey[100]}, ${theme.palette.grey[50]})`,
                ...theme.applyStyles('dark', {
                  background: alpha('#1a1a1a', 0.4),
                }),
              })}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('orders.noUnpaidInstallments')}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 1 }}>
              {installments.map((installment, index) => {
                // An installment amount can be null, since full_amount is optional.
                const parsedAmount = typeof installment.amount === 'string'
                  ? parseFloat(installment.amount)
                  : installment.amount;
                const amount = typeof parsedAmount === 'number' && !Number.isNaN(parsedAmount)
                  ? parsedAmount
                  : 0;
                return (
                  <Fade in={true} timeout={300 + index * 100} key={installment.id || index}>
                    <Box>
                      <ListItem
                        disablePadding
                        sx={{
                          mb: index < installments.length - 1 ? 2 : 0,
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleInstallmentClick(installment)}
                          sx={(theme) => ({
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'primary.light',
                            p: 2,
                            transition: 'all 0.3s ease',
                            background: theme.palette.background.paper,
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: '0 4px 16px rgba(2, 139, 203, 0.2)',
                              transform: 'translateY(-2px)',
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}05, ${theme.palette.primary.light}10)`,
                            },
                            ...theme.applyStyles('dark', {
                              background: alpha('#1a1a1a', 0.6),
                              borderColor: alpha('#028bcb', 0.3),
                              '&:hover': {
                                background: alpha('#028bcb', 0.15),
                                borderColor: alpha('#028bcb', 0.6),
                                boxShadow: '0 4px 16px rgba(2, 139, 203, 0.3)',
                              },
                            }),
                          })}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" fontWeight={600} color="primary.main">
                                  {t('orders.installment')} #{installment.seq || index + 1}
                                </Typography>
                                <Chip
                                  label={`$${amount.toFixed(2)}`}
                                  color="primary"
                                  size="medium"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    px: 1,
                                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                  }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <CalendarTodayIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                  {t('orders.dueDate')}: {dayjs(installment.due_date).format('YYYY-MM-DD')}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </Box>
                  </Fade>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions
          sx={(theme) => ({
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            background: theme.palette.background.default,
            ...theme.applyStyles('dark', {
              background: alpha('#0a0a0a', 0.8),
              borderColor: alpha('#ffffff', 0.1),
            }),
          })}
        >
          <Button
            onClick={onClose}
            sx={(theme) => ({
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : 'grey.100',
              },
            })}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Installment Payment Dialog */}
      <InstallmentPaymentDialog
        open={paymentDialogOpen}
        installment={selectedInstallment}
        onClose={handlePaymentDialogClose}
        onSuccess={handlePaymentSuccess}
      />
    </>
  );
}
